import React, { useEffect, useState } from 'react'

import { Controller, useForm } from 'react-hook-form'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { Autocomplete } from '@material-ui/lab'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@material-ui/core'
import moment from 'moment'

import { BasePhoneInput } from 'components'
import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'

import * as service from 'service'
import constants from 'constants/index'
import helpers from 'helpers'

const EnrollmentFormDialog = ({
  open,
  enrollment,
  courseId,
  onClose,
  onSuccess,
}) => {
  const snackbar = useSnackbar()
  const [selectedDepartments, setSelectedDepartments] = useState([])

  const { control, handleSubmit, reset, formState } = useForm()

  const { response: departmentsResponse } = useFetch(
    service.dponet.departments.get,
    { perPage: 10000 },
    [],
    open,
  )

  const departmentOptions = departmentsResponse?.data?.departments ?? []

  const { DEFAULT_DDI } = constants.countries

  useEffect(() => {
    if (enrollment) {
      reset({
        name: enrollment.user?.name ?? '',
        phone: enrollment.user?.phone ?? DEFAULT_DDI,
        email: enrollment.user?.email ?? '',
        deadline: enrollment.deadline ? moment(enrollment.deadline) : null,
        notificationDate: enrollment.notificationDate
          ? moment(enrollment.notificationDate)
          : null,
      })
    }
  }, [enrollment, reset])

  useEffect(() => {
    if (!enrollment || !departmentOptions.length) return

    const currentNames = (enrollment.userDepartments ?? []).map(
      (department) => department.name,
    )
    const matched = departmentOptions.filter((option) =>
      currentNames.includes(option.name),
    )
    setSelectedDepartments(matched)
  }, [enrollment, departmentOptions.length])

  const handleClose = () => {
    reset()
    setSelectedDepartments([])
    onClose()
  }

  const onSubmit = async (data) => {
    try {
      await service.dponet.trainingEnrollment.update({
        courseId,
        enrollmentId: enrollment.id,
        enrollment: {
          name: data.name,
          phone: data.phone.length < 11 ? null : data.phone,
          email: data.email,
          deadline: data.deadline ? data.deadline.format('YYYY-MM-DD') : '',
          notificationDate: data.notificationDate
            ? data.notificationDate.format('YYYY-MM-DD')
            : '',
          department_names: selectedDepartments.map(
            (department) => department.name,
          ),
        },
      })
      reset()
      onSuccess()
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao atualizar aluno.',
        variant: 'error',
      })
    }
  }

  const emailFilled = !!enrollment?.user?.email?.trim()
  const phoneFilled = (enrollment?.user?.phone?.trim()?.length ?? 0) > 8

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Editar aluno</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Altere os campos necessários para atualizar o cadastro no sistema.
          Certifique-se de salvar as alterações.
        </Typography>
        <Grid container spacing={2} style={{ marginTop: 4 }}>
          <Grid item xs={12} sm={6}>
            <Controller
              name="name"
              control={control}
              defaultValue=""
              as={<TextField label="Nome" variant="outlined" fullWidth />}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="phone"
              control={control}
              defaultValue=""
              as={
                <BasePhoneInput
                  label="Telefone"
                  variant="outlined"
                  fullWidth
                  disabled={phoneFilled}
                />
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="email"
              control={control}
              defaultValue=""
              as={
                <TextField
                  label="E-mail"
                  variant="outlined"
                  fullWidth
                  disabled={emailFilled}
                />
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="notificationDate"
              control={control}
              defaultValue={null}
              as={
                <KeyboardDatePicker
                  label="Notificar em"
                  format="DD/MM/yyyy"
                  fullWidth
                  inputVariant="outlined"
                  variant="inline"
                  clearable
                  autoOk
                />
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="deadline"
              control={control}
              defaultValue={null}
              as={
                <KeyboardDatePicker
                  label="Prazo para visualização"
                  format="DD/MM/yyyy"
                  fullWidth
                  inputVariant="outlined"
                  variant="inline"
                  autoOk
                  clearable
                />
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={departmentOptions}
              getOptionLabel={(option) => option.name}
              getOptionSelected={(option, value) => option.id === value.id}
              value={selectedDepartments}
              onChange={(event, newValue) => setSelectedDepartments(newValue)}
              renderInput={(inputProps) => (
                <TextField
                  {...inputProps}
                  label="Departamentos"
                  variant="outlined"
                  fullWidth
                  placeholder="Buscar departamentos..."
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={formState.isSubmitting}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          color="primary"
          disabled={formState.isSubmitting}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EnrollmentFormDialog
